import { useContext } from 'react'
import { createStore, useStore } from 'zustand'
import { ColumnStoreInternal } from './ColumnsStore'
import { ColumnStoreContext } from './ColumnsStoreProvider'

/**
 * Stable fallback store used when a component renders outside a
 * `ColumnsStoreProvider`. Having a real (empty) store to read from lets us call
 * `useStore` unconditionally, so hook order stays consistent.
 */
const noopColumnStore = createStore<ColumnStoreInternal>(() => ({
  groups: [],
  tableKey: '__noop__',
  selectedColumns: undefined,
  defaultSelectedColumns: undefined,
  setSelectedColumns: () => {},
  initializeTableColumns: () => {},
}))

/**
 * Like `useColumnStoreContext`, but returns `undefined` instead of throwing when
 * there is no surrounding `ColumnsStoreProvider`.
 *
 * Shared components (e.g. `BaseExportModal`) render both inside a provider
 * (every table's export modal) and outside one (the importer flows and the
 * sample-request export, which build their column list locally and pass no
 * `selectedColumns`). Those provider-less callers must keep working unchanged.
 *
 * `useStore` is always invoked — against the real store when present, otherwise
 * the stable noop store — so the rules of hooks are satisfied without an
 * exception. We return `undefined` when no provider was found.
 */
export function useOptionalColumnStoreContext<T>(
  selector: (state: ColumnStoreInternal) => T
): T | undefined {
  const store = useContext(ColumnStoreContext)
  const value = useStore(store ?? noopColumnStore, selector)
  return store ? value : undefined
}
